import moment from 'moment';

type DateRangeBounds = {
  start: Date;
  end: Date;
};

export const ANALYTICS_RANGE_OPTIONS = [
  { value: 'TODAY', label: 'Today' },
  { value: 'YESTERDAY', label: 'Yesterday' },
  { value: 'ONE_WEEK', label: '1 Week' },
  { value: 'ONE_MONTH', label: '1 Month' },
  { value: 'LAST_6_MONTHS', label: 'Last 6 Months' },
  { value: 'LAST_1_YEAR', label: 'Last 1 Year' },
  { value: 'ALL_TIME', label: 'All Time' },
  { value: 'CUSTOM', label: 'Custom Range' },
] as const;

const startOfDay = (date: Date): Date => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfDay = (date: Date): Date => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

export const getDateRangeBounds = (range: string): DateRangeBounds | null => {
  const today = new Date();

  switch (range) {
    case 'TODAY':
      return {
        start: startOfDay(today),
        end: endOfDay(today),
      };
    case 'YESTERDAY': {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return {
        start: startOfDay(yesterday),
        end: endOfDay(yesterday),
      };
    }
    case 'ONE_WEEK':
      return {
        start: startOfDay(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)),
        end: endOfDay(today),
      };
    case 'ONE_MONTH': {
      const start = new Date(today);
      start.setMonth(today.getMonth() - 1);
      start.setDate(1);
      return {
        start: startOfDay(start),
        end: endOfDay(today),
      };
    }
    case 'LAST_6_MONTHS': {
      const start = new Date(today);
      start.setMonth(today.getMonth() - 6);
      return {
        start: startOfDay(start),
        end: endOfDay(today),
      };
    }
    case 'LAST_1_YEAR': {
      const start = new Date(today);
      start.setFullYear(today.getFullYear() - 1);
      return {
        start: startOfDay(start),
        end: endOfDay(today),
      };
    }
    case 'ALL_TIME':
    case 'CUSTOM':
      return null;
    default:
      return null;
  }
};

export const formatDateRangeLabel = (
  range: string,
  customStart?: string,
  customEnd?: string
): string => {
  if (range === 'ALL_TIME') {
    return 'All time';
  }

  if (range === 'CUSTOM') {
    if (customStart && customEnd) {
      const startLabel = moment(customStart).format('MMM D, YYYY');
      const endLabel = moment(customEnd).format('MMM D, YYYY');
      return `${startLabel} - ${endLabel}`;
    }

    return 'Select start and end dates';
  }

  const bounds = getDateRangeBounds(range);
  if (!bounds) {
    return '';
  }

  const startLabel = moment(bounds.start).format('MMM D, YYYY');
  const endLabel = moment(bounds.end).format('MMM D, YYYY');

  return `${startLabel} - ${endLabel}`;
};

export const getRangeOptionLabel = (
  value: string,
  label: string,
  customStart?: string,
  customEnd?: string
): string => {
  if (value === 'CUSTOM') {
    return label;
  }

  const dateLabel = formatDateRangeLabel(value, customStart, customEnd);
  return dateLabel ? `${label} (${dateLabel})` : label;
};

export const buildAnalyticsQuery = (
  range: string,
  startDate?: string,
  endDate?: string
): string => {
  if (range === 'CUSTOM') {
    if (!startDate || !endDate) {
      return '';
    }

    return `startDate=${encodeURIComponent(
      startDate
    )}&endDate=${encodeURIComponent(endDate)}`;
  }

  return `range=${range}`;
};

export const isAnalyticsQueryReady = (
  range: string,
  startDate?: string,
  endDate?: string
): boolean => {
  if (range === 'CUSTOM') {
    return Boolean(startDate && endDate);
  }

  return Boolean(range);
};
